import { isBefore } from 'date-fns';

import { FeedbackSchedule } from 'types/questions.types';
import {
  EventPublishSchedule,
  EventPublishScheduleStatus,
  Reminder,
  EmailReminder
} from 'types/event.types';
import { parseUTCDate } from 'utils/dateTimeUtils';
import dayjs from 'utils/extendedDayJS';

const PENDING_PUBLISH_STATUSES: EventPublishScheduleStatus[] = [
  EventPublishScheduleStatus.SCHEDULED,
  EventPublishScheduleStatus.QUEUED,
  EventPublishScheduleStatus.PROCESSING
];

/**
 * Returns the scheduled publish time if the event has a pending publish schedule.
 * Used for validating that scheduled features (feedback, reminders) are set after
 * the event is scheduled to be published.
 */
export const getPendingScheduledPublishAt = (
  publishSchedule: EventPublishSchedule | null | undefined
): string | undefined => {
  if (publishSchedule?.status && PENDING_PUBLISH_STATUSES.includes(publishSchedule.status)) {
    return publishSchedule.scheduledAt;
  }
  return undefined;
};

/**
 * Represents a scheduled communication that conflicts with the publish time
 */
export interface ScheduledCommunicationConflict {
  type: 'slack_reminder' | 'email_reminder' | 'feedback';
  name: string;
  scheduledAt: string; // UTC datetime string
}

// notifyStatus 0 = SCHEDULED (not yet sent)
const REMINDER_STATUS_SCHEDULED = 0;

/**
 * Calculates the scheduled time for a reminder based on absolute or relative schedule
 */
const calculateReminderScheduledTime = (
  reminder: Reminder | EmailReminder,
  datetimeScheduled: string
): string | null => {
  // If absolute datetime exists, use it
  if (reminder.datetime) {
    return reminder.datetime;
  }

  // If relative schedule exists, calculate the actual time
  if (reminder.schedule?.quantity && reminder.schedule?.unit) {
    const beforeEventBegins = reminder.schedule.beforeEventBegins ?? true;
    return dayjs.subtractOrAddTimeFromDateTime(
      datetimeScheduled,
      reminder.schedule.quantity,
      reminder.schedule.unit,
      beforeEventBegins,
      'UTC'
    );
  }

  return null;
};

/**
 * Calculates the scheduled time for feedback based on absolute or relative schedule
 */
const calculateFeedbackScheduledTime = (
  feedbackSchedule: FeedbackSchedule,
  datetimeEnd: string
): string | null => {
  // If absolute sendAt exists, use it
  if (feedbackSchedule.sendAt) {
    return feedbackSchedule.sendAt;
  }

  // If relative schedule exists, calculate the actual time
  if (feedbackSchedule.quantity && feedbackSchedule.unit) {
    const beforeEventEnds = feedbackSchedule.beforeEventEnds ?? true;
    return dayjs.subtractOrAddTimeFromDateTime(
      datetimeEnd,
      feedbackSchedule.quantity,
      feedbackSchedule.unit,
      beforeEventEnds,
      'UTC'
    );
  }

  return null;
};

/**
 * Returns communications that would conflict with a given publish time.
 * A conflict occurs when a scheduled communication is set to send before the event is published.
 */
export const getScheduledCommunicationConflicts = ({
  slackReminders,
  emailReminders,
  feedbackSchedule,
  feedbackEnable,
  datetimeScheduled,
  datetimeEnd,
  publishAtUTC
}: {
  slackReminders?: Reminder[];
  emailReminders?: EmailReminder[];
  feedbackSchedule?: FeedbackSchedule;
  feedbackEnable?: boolean | null;
  datetimeScheduled?: string | null;
  datetimeEnd?: string | null;
  publishAtUTC: string;
}): ScheduledCommunicationConflict[] => {
  const conflicts: ScheduledCommunicationConflict[] = [];
  const publishTime = dayjs.utc(publishAtUTC);

  // Check Slack reminders
  if (slackReminders && datetimeScheduled) {
    slackReminders.forEach((reminder, index) => {
      // Only check scheduled reminders (notifyStatus === 0)
      if (reminder.notifyStatus !== REMINDER_STATUS_SCHEDULED) {
        return;
      }

      const scheduledAt = calculateReminderScheduledTime(reminder, datetimeScheduled);
      if (scheduledAt && dayjs.utc(scheduledAt).isBefore(publishTime)) {
        conflicts.push({
          type: 'slack_reminder',
          name: reminder.title || `Slack reminder #${index + 1}`,
          scheduledAt
        });
      }
    });
  }

  // Check Email reminders
  if (emailReminders && datetimeScheduled) {
    emailReminders.forEach((reminder, index) => {
      // Only check scheduled reminders (notifyStatus === 0)
      if (reminder.notifyStatus !== REMINDER_STATUS_SCHEDULED) {
        return;
      }

      const scheduledAt = calculateReminderScheduledTime(reminder, datetimeScheduled);
      if (scheduledAt && dayjs.utc(scheduledAt).isBefore(publishTime)) {
        conflicts.push({
          type: 'email_reminder',
          name: reminder.subject || `Email reminder #${index + 1}`,
          scheduledAt
        });
      }
    });
  }

  // Check Feedback schedule (only if feedback is enabled)
  if (feedbackEnable && feedbackSchedule && datetimeEnd) {
    const scheduledAt = calculateFeedbackScheduledTime(feedbackSchedule, datetimeEnd);
    if (scheduledAt && dayjs.utc(scheduledAt).isBefore(publishTime)) {
      conflicts.push({
        type: 'feedback',
        name: 'Feedback form',
        scheduledAt
      });
    }
  }

  return conflicts;
};

/**
 * Checks if the provided start datetime is before the scheduled publish time.
 * Used for validation when user tries to set event/session start time before publish time.
 *
 * @param startDateTimeUTC - Start datetime in UTC format
 * @param publishSchedule - Event publish schedule
 * @returns true if start time is before scheduled publish time, false otherwise
 */
export const isStartTimeBeforeScheduledPublish = (
  startDateTimeUTC: string,
  publishSchedule: EventPublishSchedule | null | undefined
): boolean => {
  const scheduledPublishAt = getPendingScheduledPublishAt(publishSchedule);

  if (!scheduledPublishAt) {
    return false;
  }

  const startDateTime = parseUTCDate(startDateTimeUTC);
  const publishDateTime = parseUTCDate(scheduledPublishAt);

  return isBefore(startDateTime, publishDateTime);
};
